import { FEATURE_FLAG_CONFIG, FeatureFlagConfig } from './feature-flags-helper';
import { GetFeatureFlagsV2, GetFeatureFlagsV2Variables, FeatureFlagType } from '@/generated/graphql';

/**
 * A simplified / flattened representation of the feature flags returned from the GQL API, based on the feature flag
 * configuration.
 */
export type ClientFeatureFlags = {
  [K in keyof FeatureFlagConfig]: FeatureFlagConfig[K]['value'];
};

const {
  /**
   * The variables for querying the GQL API for feature flag values, based on the feature flag configuration.
   */
  FEATURE_FLAG_QUERY_VARIABLES,

  /**
   * Default values for the flags from the GQL API, based on the feature flag configuration.
   */
  DEFAULT_CLIENT_FEATURE_FLAGS,

  // For internal use, a map of server keys (from the config) to the corresponding client object key
  serverKeyToClientKeyMap,
} = getVariablesAndDefaultFlags();

export { FEATURE_FLAG_QUERY_VARIABLES, DEFAULT_CLIENT_FEATURE_FLAGS };

/**
 * Maps the query response from the GQL API to the client-side representation of flags (i.e. ClientFeatureFlags).
 * Returns the default values if the response is undefined.
 */
export function mapFeatureFlagResponse(response?: GetFeatureFlagsV2): ClientFeatureFlags {
  if (response === undefined) {
    return DEFAULT_CLIENT_FEATURE_FLAGS;
  }

  const { featureFlagsV2 } = response;
  // Start with the default values for all flags, then override with values from the server
  const result: Record<ClientKey, string | number | boolean> = { ...DEFAULT_CLIENT_FEATURE_FLAGS };

  for (const { key: serverKey, value } of featureFlagsV2) {
    const clientKey = serverKeyToClientKeyMap.get(serverKey);
    if (clientKey === undefined) {
      continue;
    }

    if ('boolValue' in value) {
      result[clientKey] = value.boolValue;
      continue;
    }

    if ('stringValue' in value) {
      result[clientKey] = value.stringValue;
      continue;
    }

    result[clientKey] = value.intValue;
  }

  return result as ClientFeatureFlags;
}

type ClientKey = keyof ClientFeatureFlags;

// Helper function to get variables / defaults based on the config
function getVariablesAndDefaultFlags() {
  const flags: GetFeatureFlagsV2Variables['flags'] = [];
  const defaultFlags: Record<string, string | number | boolean> = {};
  const serverKeyToClientKeyMap = new Map<string, ClientKey>();

  for (const prop in FEATURE_FLAG_CONFIG) {
    const clientKey = prop as ClientKey;
    const { key: serverKey, value: defaultValue } = FEATURE_FLAG_CONFIG[clientKey];

    // Add to the query variables with the correct type based on the default value
    let flagType: FeatureFlagType;
    switch (typeof defaultValue) {
      case 'boolean':
        flagType = FeatureFlagType.BOOL;
        break;
      case 'string':
        flagType = FeatureFlagType.STRING;
        break;
      case 'number':
        flagType = FeatureFlagType.INT;
        break;
    }

    flags.push({ key: serverKey, flagType });

    // Add the default value from the config
    defaultFlags[clientKey] = defaultValue;

    // Add to the map of server key => client key
    serverKeyToClientKeyMap.set(serverKey, clientKey);
  }

  const DEFAULT_CLIENT_FEATURE_FLAGS = defaultFlags as ClientFeatureFlags;
  const FEATURE_FLAG_QUERY_VARIABLES: GetFeatureFlagsV2Variables = { flags };
  return {
    DEFAULT_CLIENT_FEATURE_FLAGS,
    FEATURE_FLAG_QUERY_VARIABLES,
    serverKeyToClientKeyMap,
  };
}
